import React, { useState, useEffect, useRef, useCallback } from "react";
import { SimpleModulePage } from "../modules/SimpleModulePage";
import { useAuth } from "../context/AuthContext";

const SectionCard: React.FC<{ children: React.ReactNode; className?: string }> = ({ children, className = "" }) => (
  <div className={`mx-4 mt-3 rounded-2xl bg-white shadow-sm px-4 py-3 mb-3 border border-slate-100 ${className}`}>{children}</div>
);

interface MedicationHistoryPageProps {
  onBack: () => void;
}

export const MedicationHistoryPage: React.FC<MedicationHistoryPageProps> = ({ onBack }) => {
  const { loadMedicationList, loadMedicationRecords } = useAuth();
  const [medications, setMedications] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [selectedMedication, setSelectedMedication] = useState<any | null>(null);
  const [medicationRecords, setMedicationRecords] = useState<any[]>([]);
  const [offset, setOffset] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  const loadMedicationListData = useCallback(async (currentOffset: number) => {
    if (currentOffset === 0) {
      setLoading(true);
    } else {
      setLoadingMore(true);
    }

    try {
      const result = await loadMedicationList(10, currentOffset);
      console.log('[MedicationHistory] API Response:', result);
      if (result && result.success) {
        const meds = result.data || [];
        console.log('[MedicationHistory] Medications loaded:', meds.length, meds);
        if (currentOffset === 0) {
          setMedications(meds);
        } else {
          setMedications((prev) => [...prev, ...meds]);
        }
        setHasMore(meds.length === 10);
        setOffset(currentOffset + 10);
      }
    } catch (error) {
      console.error('[MedicationHistory] Error loading medications:', error);
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, [loadMedicationList]);

  // 초기 약물 목록 로드
  useEffect(() => {
    loadMedicationListData(0);
  }, [loadMedicationListData]);

  // 약물 선택 시 기록 로드
  const handleMedicationClick = async (medication: any) => {
    console.log('[MedicationHistory] Selected medication:', medication);
    console.log('[MedicationHistory] start_date:', medication.start_date, 'type:', typeof medication.start_date);
    console.log('[MedicationHistory] end_date:', medication.end_date, 'type:', typeof medication.end_date);
    setSelectedMedication(medication);
    try {
      const result = await loadMedicationRecords(medication.id);
      console.log('[MedicationHistory] Records loaded:', result);
      if (result && result.success) {
        setMedicationRecords(result.data || []);
      }
    } catch (error) {
      console.error('[MedicationHistory] Error loading records:', error);
    }
  };

  // 무한 스크롤
  const handleScroll = useCallback(() => {
    if (!scrollContainerRef.current) return;
    
    const container = scrollContainerRef.current;
    if (
      container.scrollTop + container.clientHeight >= container.scrollHeight - 100 &&
      hasMore &&
      !loadingMore &&
      !loading
    ) {
      loadMedicationListData(offset);
    }
  }, [offset, hasMore, loadingMore, loading, loadMedicationListData]);

  useEffect(() => {
    const container = scrollContainerRef.current;
    if (container) {
      container.addEventListener('scroll', handleScroll);
      return () => container.removeEventListener('scroll', handleScroll);
    }
  }, [handleScroll]);

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return "-";
    try {
      // 다양한 날짜 포맷 지원
      let date: Date;
      
      if (typeof dateStr === 'string') {
        // YYYY-MM-DD HH:MM:SS 형식 (타임스탬프)
        if (dateStr.includes(' ')) {
          date = new Date(dateStr.replace(' ', 'T'));
        }
        // ISO 형식 (YYYY-MM-DDTHH:mm:ss)
        else if (dateStr.includes('T')) {
          date = new Date(dateStr);
        }
        // YYYY-MM-DD 형식
        else if (dateStr.match(/^\d{4}-\d{2}-\d{2}$/)) {
          date = new Date(dateStr + 'T00:00:00');
        }
        // 기타 형식 시도
        else {
          date = new Date(dateStr);
        }

        // 유효한 날짜 확인
        if (isNaN(date.getTime())) {
          console.warn('[formatDate] Invalid date:', dateStr);
          return dateStr; // 원본 문자열 반환
        }

        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, "0");
        const day = String(date.getDate()).padStart(2, "0");
        return `${year}년 ${month}월 ${day}일`;
      }

      return "-";
    } catch (error) {
      console.error('[formatDate] Error:', error, dateStr);
      return dateStr || "-";
    }
  };

  const formatDateTime = (dateTimeStr: string | null) => {
    if (!dateTimeStr) return "-";
    try {
      let date: Date;

      if (typeof dateTimeStr === 'string') {
        // YYYY-MM-DD HH:MM:SS 형식
        if (dateTimeStr.includes(' ')) {
          date = new Date(dateTimeStr.replace(' ', 'T'));
        }
        // ISO 형식
        else if (dateTimeStr.includes('T')) {
          date = new Date(dateTimeStr);
        } else {
          date = new Date(dateTimeStr);
        }

        if (isNaN(date.getTime())) {
          console.warn('[formatDateTime] Invalid datetime:', dateTimeStr);
          return dateTimeStr;
        }

        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, "0");
        const day = String(date.getDate()).padStart(2, "0");
        const hour = String(date.getHours()).padStart(2, "0");
        const minute = String(date.getMinutes()).padStart(2, "0");
        return `${year}년 ${month}월 ${day}일 ${hour}:${minute}`;
      }

      return "-";
    } catch (error) {
      console.error('[formatDateTime] Error:', error, dateTimeStr);
      return dateTimeStr || "-";
    }
  };

  // 상세 뷰
  if (selectedMedication) {
    return (
      <SimpleModulePage
        title={selectedMedication.medication_name || "약물 상세"}
        subtitle={`${formatDate(selectedMedication.start_date)} ~ ${selectedMedication.end_date ? formatDate(selectedMedication.end_date) : "계속"}`}
        icon="💊"
        onBack={() => {
          setSelectedMedication(null);
          setMedicationRecords([]);
        }}
      >
        {/* 약물 정보 */}
        <SectionCard>
          <div className="space-y-2 mb-4">
            <div className="flex justify-between items-start">
              <span className="text-[12px] text-slate-600">약물명</span>
              <span className="text-[13px] font-semibold text-slate-900">{selectedMedication.medication_name}</span>
            </div>
            <div className="flex justify-between items-start">
              <span className="text-[12px] text-slate-600">용량</span>
              <span className="text-[13px] text-slate-700">{selectedMedication.dosage}</span>
            </div>
            <div className="flex justify-between items-start">
              <span className="text-[12px] text-slate-600">복용 단위</span>
              <span className="text-[13px] text-slate-700">
                {selectedMedication.dose_amount || 1} {selectedMedication.dose_unit || "정"}
              </span>
            </div>
            <div className="flex justify-between items-start">
              <span className="text-[12px] text-slate-600">식사 관계</span>
              <span className="text-[13px] text-slate-700">
                {selectedMedication.meal_relation === "before_meal"
                  ? "식전"
                  : selectedMedication.meal_relation === "with_meal"
                  ? "식중"
                  : selectedMedication.meal_relation === "after_meal"
                  ? "식후"
                  : selectedMedication.meal_relation === "between_meals"
                  ? "공복"
                  : "상관없음"}
              </span>
            </div>
            <div className="flex justify-between items-start">
              <span className="text-[12px] text-slate-600">복용 횟수</span>
              <span className="text-[13px] text-slate-700">{selectedMedication.frequency_type || "-"}</span>
            </div>
            {selectedMedication.purpose && (
              <div className="flex justify-between items-start">
                <span className="text-[12px] text-slate-600">목적</span>
                <span className="text-[13px] text-slate-700">{selectedMedication.purpose}</span>
              </div>
            )}
          </div>
        </SectionCard>

        {/* 복용 기록 */}
        <SectionCard>
          <p className="text-[13px] font-semibold text-slate-900 mb-3">
            복용 기록 ({medicationRecords.length}건)
          </p>
          {medicationRecords.length === 0 ? (
            <p className="text-center text-[12px] text-slate-600 py-4">
              등록된 복용 기록이 없습니다.
            </p>
          ) : (
            <div className="space-y-2">
              {medicationRecords.map((record, idx) => (
                <div key={idx} className="p-3 border border-slate-200 rounded-lg">
                  <div className="flex items-start justify-between mb-2">
                    <div>
                      <p className="text-[12px] text-slate-600">
                        {formatDateTime(record.actual_datetime || record.scheduled_datetime)}
                      </p>
                    </div>
                    <span
                      className={`text-[10px] px-2 py-1 rounded-full font-semibold ${
                        record.status === "taken"
                          ? "bg-emerald-100 text-emerald-700"
                          : record.status === "missed"
                          ? "bg-red-100 text-red-700"
                          : record.status === "skipped"
                          ? "bg-slate-100 text-slate-700"
                          : "bg-amber-100 text-amber-700"
                      }`}
                    >
                      {record.status === "taken"
                        ? "복용"
                        : record.status === "missed"
                        ? "미복용"
                        : record.status === "skipped"
                        ? "생략"
                        : "보류"}
                    </span>
                  </div>
                  {record.delay_minutes && (
                    <p className="text-[11px] text-slate-500">
                      지연: {record.delay_minutes}분
                    </p>
                  )}
                  {record.notes && <p className="text-[11px] text-slate-600 mt-1">{record.notes}</p>}
                </div>
              ))}
            </div>
          )}
        </SectionCard>
      </SimpleModulePage>
    );
  }

  // 목록 뷰
  return (
    <SimpleModulePage
      title="복약&처방전"
      subtitle="처방약 관리"
      icon="💊"
      onBack={onBack}
    >
      {loading ? (
        <SectionCard>
          <p className="text-center text-[13px] text-slate-600">로딩 중...</p>
        </SectionCard>
      ) : medications.length === 0 ? (
        <SectionCard>
          <p className="text-center text-[13px] text-slate-600">등록된 처방약이 없습니다.</p>
        </SectionCard>
      ) : (
        <div ref={scrollContainerRef} style={{ maxHeight: "calc(100vh - 200px)", overflowY: "auto" }}>
          <SectionCard>
            <div className="space-y-0">
              {medications.map((med, idx) => (
                <button
                  key={med.id}
                  onClick={() => handleMedicationClick(med)}
                  className={`w-full py-3 px-0 text-left border-b border-slate-100 ${
                    idx === medications.length - 1 ? "last:border-b-0" : ""
                  } hover:bg-slate-50 transition-colors flex items-center justify-between`}
                >
                  <div>
                    <p className="text-[13px] font-semibold text-slate-900 mb-1">
                      {med.medication_name}
                    </p>
                    <p className="text-[11px] text-slate-600 mb-1">{med.dosage}</p>
                    <p className="text-[10px] text-slate-500">
                      {formatDate(med.start_date)} ~ {med.end_date ? formatDate(med.end_date) : "계속"}
                    </p>
                  </div>
                  <span className="text-slate-400">→</span>
                </button>
              ))}
            </div>
          </SectionCard>

          {hasMore && (
            <SectionCard>
              <p className="text-center text-[12px] text-slate-500">
                {loadingMore ? "로딩 중..." : "스크롤하여 더 보기"}
              </p>
            </SectionCard>
          )}
        </div>
      )}

      {/* 안내 */}
      <SectionCard className="bg-blue-50 border-blue-200">
        <div className="flex gap-2">
          <span className="text-[16px] flex-shrink-0">ℹ️</span>
          <div>
            <p className="text-[12px] font-semibold text-blue-900 mb-1">정보 안내</p>
            <p className="text-[11px] text-blue-800 leading-relaxed">
              등록된 처방약을 확인할 수 있습니다. 각 약물을 클릭하여 복용 기록을 확인하세요.
            </p>
          </div>
        </div>
      </SectionCard>
    </SimpleModulePage>
  );
};
